if (!customElements.get('native-product-coupon')) {
  customElements.define(
    'native-product-coupon',
    class NativeProductCoupon extends HTMLElement {
      connectedCallback() {
        this.checkbox = this.querySelector('[data-coupon-checkbox]');
        this.label = this.querySelector('[data-coupon-label]');
        this.liveRegion = this.querySelector('[data-coupon-live]');
        this.errorMessage = this.querySelector('[data-coupon-error]');
        this.discountCode = this.dataset.discountCode?.trim();
        this.discountPercent = Math.min(Math.max(parseFloat(this.dataset.discountPercent) || 0, 0), 100);
        this.sectionId = this.dataset.sectionId;
        this.productId = this.dataset.productId;
        this.basePrice = parseInt(this.dataset.productPrice) || 0;
        this.bundlePrice = null;
        this.priceSnapshots = new WeakMap();
        this.productForm = document.getElementById(this.dataset.formId);

        if (!this.checkbox || !this.discountCode) return;

        this.onChange = this.handleChange.bind(this);
        this.onVariantChange = this.handleVariantChange.bind(this);
        this.onBundleChange = this.handleBundleChange.bind(this);
        this.checkbox.addEventListener('change', this.onChange);
        this.productForm?.addEventListener('variant:change', this.onVariantChange);
        this.productForm?.addEventListener('bundle:change', this.onBundleChange);
        this.syncFromCart();
      }

      disconnectedCallback() {
        this.checkbox?.removeEventListener('change', this.onChange);
        this.productForm?.removeEventListener('variant:change', this.onVariantChange);
        this.productForm?.removeEventListener('bundle:change', this.onBundleChange);
      }

      get rootUrl() {
        return window.Shopify?.routes?.root || '/';
      }

      get cartUrl() {
        return window.theme?.routes?.cart_url || `${this.rootUrl}cart`;
      }

      get cartUpdateUrl() {
        return window.theme?.routes?.cart_update_url || `${this.rootUrl}cart/update`;
      }

      async readCart() {
        const response = await fetch(`${this.cartUrl}.js`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) throw new Error('Unable to read cart');
        return response.json();
      }

      discountCodes(cart) {
        if (!Array.isArray(cart?.discount_codes)) return [];
        return cart.discount_codes.map((discount) => discount.code).filter(Boolean);
      }

      includesCode(codes) {
        return codes.some((code) => code.toLowerCase() === this.discountCode.toLowerCase());
      }

      setLoading(loading) {
        this.toggleAttribute('aria-busy', loading);
        this.checkbox.disabled = loading;
      }

      setActiveState(active) {
        this.checkbox.checked = active;
        this.toggleAttribute('data-active', active);
        if (this.label) {
          this.label.textContent = active
            ? this.dataset.activeLabel || this.dataset.defaultLabel
            : this.dataset.defaultLabel;
        }
        this.renderPriceState(active);
      }

      handleVariantChange(event) {
        const variantPrice = parseInt(event.detail?.variant?.price);
        if (Number.isFinite(variantPrice)) {
          this.basePrice = variantPrice;
          this.bundlePrice = null;
        }

        requestAnimationFrame(() => this.renderPriceState(this.checkbox.checked));
      }

      handleBundleChange(event) {
        const items = Array.isArray(event.detail?.items) ? event.detail.items : [];
        this.bundlePrice = items.reduce((total, item) => {
          const price = parseInt(item.price) || 0;
          const quantity = parseInt(item.quantity) || 0;
          return total + (price * quantity);
        }, this.basePrice);

        requestAnimationFrame(() => this.renderBuyButtonPrice(this.checkbox.checked));
      }

      discountedPrice(price) {
        return Math.round(price * (100 - this.discountPercent) / 100);
      }

      formatMoney(cents) {
        const format = window.theme?.settings?.currencyCodeEnabled
          ? window.theme.settings.moneyWithCurrencyFormat
          : window.theme?.settings?.moneyFormat;

        if (window.theme?.Currency?.formatMoney && format) {
          return window.theme.Currency.formatMoney(cents, format);
        }

        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
        }).format(cents / 100);
      }

      priceContainers() {
        if (!this.sectionId || !this.productId) return [];

        return [
          document.getElementById(`Price-${this.sectionId}-${this.productId}`),
          document.getElementById(`StickyPrice-${this.sectionId}-${this.productId}`),
        ].filter(Boolean);
      }

      renderPriceState(active) {
        if (!this.discountPercent || !this.basePrice) return;

        this.priceContainers().forEach((container) => {
          const priceElement = container.matches('.price') ? container : container.querySelector('.price');
          if (!priceElement) return;

          if (active) {
            if (!this.priceSnapshots.has(priceElement)) {
              this.priceSnapshots.set(priceElement, {
                className: priceElement.className,
                innerHTML: priceElement.innerHTML,
              });
            }

            const snapshot = this.priceSnapshots.get(priceElement);
            priceElement.className = snapshot.className;
            priceElement.classList.add('price--on-sale', 'gm-native-coupon-price');
            priceElement.innerHTML = `
              <span class="sr-only">Discounted price</span>
              <span class="price__regular whitespace-nowrap">${this.formatMoney(this.discountedPrice(this.basePrice))}</span>
              <span class="sr-only">Price before coupon</span>
              <span class="price__sale inline-flex items-center h-auto relative">${this.formatMoney(this.basePrice)}</span>
            `;
          }
          else {
            const snapshot = this.priceSnapshots.get(priceElement);
            if (!snapshot) return;

            priceElement.className = snapshot.className;
            priceElement.innerHTML = snapshot.innerHTML;
            this.priceSnapshots.delete(priceElement);
          }
        });

        this.renderBuyButtonPrice(active);
      }

      renderBuyButtonPrice(active) {
        if (!this.sectionId || !this.productId || !this.basePrice) return;

        const buyPrice = document
          .getElementById(`BuyButtonPrice-${this.sectionId}-${this.productId}`)
          ?.querySelector('product-buy-price');
        if (!buyPrice) return;

        const purchasePrice = this.bundlePrice ?? this.basePrice;
        buyPrice.innerHTML = this.formatMoney(active ? this.discountedPrice(purchasePrice) : purchasePrice);
        buyPrice.toggleAttribute('data-coupon-active', active);
      }

      announce(message) {
        if (this.liveRegion) this.liveRegion.textContent = message;
      }

      showError(message = '') {
        if (!this.errorMessage) return;
        this.errorMessage.textContent = message;
        this.errorMessage.hidden = !message;
      }

      async syncFromCart() {
        this.setLoading(true);
        try {
          const cart = await this.readCart();
          this.setActiveState(this.includesCode(this.discountCodes(cart)));
        } catch (error) {
          this.setActiveState(false);
        } finally {
          this.setLoading(false);
        }
      }

      async handleChange() {
        const shouldApply = this.checkbox.checked;
        const previousState = !shouldApply;
        this.setLoading(true);
        this.showError();

        try {
          const cart = await this.readCart();
          const existingCodes = this.discountCodes(cart);
          const codeExists = this.includesCode(existingCodes);
          const nextCodes = shouldApply
            ? codeExists ? existingCodes : [...existingCodes, this.discountCode]
            : existingCodes.filter((code) => code.toLowerCase() !== this.discountCode.toLowerCase());

          let updatedCart = cart;
          if (codeExists !== shouldApply) {
            const response = await fetch(this.cartUpdateUrl, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ discount: nextCodes.join(',') }),
            });

            if (!response.ok) throw new Error('Unable to update cart discount');
            updatedCart = await response.json();
          }

          const isSelected = this.includesCode(this.discountCodes(updatedCart));
          if (isSelected !== shouldApply) throw new Error('Discount state did not update');

          this.setActiveState(isSelected);
          this.announce(
            isSelected
              ? `${this.discountCode} discount selected for your cart.`
              : `${this.discountCode} discount removed from your cart.`
          );
          this.publishCartUpdate(updatedCart);
        } catch (error) {
          this.setActiveState(previousState);
          this.showError('We could not update this offer. Please try again.');
          this.announce('The offer could not be updated. Please try again.');
        } finally {
          this.setLoading(false);
        }
      }

      publishCartUpdate(cart) {
        if (window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'native-product-coupon',
            cart,
          });
        }

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { open: false },
          })
        );
      }
    }
  );
}
